import React, { useState, useRef } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import * as Sentry from '@sentry/react'
import { useSnackbar } from 'notistack'
import type { QuoteTag } from 'paintscout'
import { getTags } from '@paintscout/util/builder'
import isEqual from 'lodash/isEqual'
import CloseIcon from '@material-ui/icons/Close'
import type { StyleClasses } from '@paintscout/ui/styles'
import type { DialogProps } from '@paintscout/ui/components/Dialog'
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  ConfirmationDialog,
  IconButton
} from '@paintscout/ui'
import TagSelection from '../components/TagSelection'
import { useClientOptions } from '../providers/ClientOptionsProvider'
import { useDialogs } from '../providers/DialogStack'

const useStyles = makeStyles<Theme, EditTagsDialogProps>(
  (_theme) => ({
    root: {
      overflow: 'visible'
    },
    dialogTitle: {},
    dialogContent: {
      overflow: 'visible'
    },
    cancel: {},
    confirm: {}
  }),
  { name: 'EditTagsDialog' }
)

export interface EditTagsDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  appliedTags: string[]
  onConfirm: (tags: string[]) => any
  onCancel: (event: React.MouseEvent<HTMLElement>) => any
  confirmLabel?: string
}

function EditTagsDialog(props: EditTagsDialogProps) {
  const classes = useStyles(props)
  const { onConfirm, onCancel, appliedTags, confirmLabel = 'Confirm', ...baseDialogProps } = props
  const { enqueueSnackbar } = useSnackbar()
  const { options } = useClientOptions()
  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()
  const [isLoading, setIsLoading] = useState(false)
  const initialTags = getTags({ options, keys: appliedTags })
  const initialTagsRef = useRef<QuoteTag[]>(initialTags)
  const [currentTags, setCurrentTags] = useState<QuoteTag[]>(initialTags)

  function hasChanges(): boolean {
    const currentTagNames = currentTags.map((tag) => tag.name).sort()
    const initialTagNames = initialTagsRef.current.map((tag) => tag.name).sort()
    return !isEqual(currentTagNames, initialTagNames)
  }

  function handleClose() {
    if (hasChanges()) {
      openDialog(ConfirmationDialog, {
        message: 'You have unsaved changes. Are you sure you want to close?',
        onConfirm: () => {
          dismissDialog() // Dismiss confirmation dialog
          dismissAllDialogs() // Close all dialogs
        },
        onCancel: () => {
          dismissDialog()
        }
      })
    } else {
      if (onCancel) {
        onCancel({} as React.MouseEvent<HTMLElement>)
      } else {
        dismissAllDialogs()
      }
    }
  }

  return (
    <Dialog {...baseDialogProps} classes={{ paper: classes.root }} onClose={handleClose}>
      <DialogTitle
        rightContent={
          <IconButton size="small" onClick={handleClose} disabled={isLoading}>
            <CloseIcon />
          </IconButton>
        }
        classes={{ root: classes.dialogTitle }}
      >
        <Typography variant="h2">Edit Tags</Typography>
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <TagSelection
          label="Tags"
          appliedTags={currentTags}
          handleChange={(tags: QuoteTag[]) => {
            setCurrentTags(tags)
          }}
          hideCloseButton
        />
      </DialogContent>
      <DialogActions>
        <Button
          className={classes.confirm}
          onClick={handleSubmit}
          form="edit-tags-dialog-form"
          variant={'contained'}
          disabled={isLoading}
          data-testid="edit-tags-dialog-confirm-button"
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )

  async function handleSubmit() {
    setIsLoading(true)
    try {
      onConfirm(currentTags.map((tag) => tag.name))
    } catch (error) {
      Sentry.captureException(error)
      enqueueSnackbar('error', { variant: 'error' })
    }
    setIsLoading(false)
  }
}

export default EditTagsDialog
